import { StatusOverlay, type StatusOverlayOptions, type StatusTheme } from './status-overlay';

// ============================================================================
// Antumo ReconnectOverlay — Blazor Server reconnect UI on top of StatusOverlay
// ============================================================================
//
// Drop-in replacement for the .NET 10 template's ReconnectModal script. Markup:
//
//     <dialog id="components-reconnect-modal" data-antumo-reconnect data-nosnippet></dialog>
//
// (rendered by the Blazor component <AntumoReconnectModal />). autoInit() wires
// every such dialog; Blazor finds it by id and sends
// `components-reconnect-state-changed` events to it.
//
// Behaviour beyond the template:
// - Nothing is shown for the first `showDelayMs` (a backgrounded tab usually
//   reconnects in a fraction of a second: no flash).
// - While the overlay is up, `healthUrl` is polled every `pollMs`. Blazor waits up
//   to 30 s between its own attempts, so after a server restart the page used to
//   come back only on the next attempt; now it reconnects as soon as the server
//   answers.
// - When the circuit is gone (server restarted / circuit expired: states `failed`,
//   `rejected`, `resume-failed`, or `paused` that does not clear) the page is
//   reloaded as soon as the server answers, never left on a dead „Resume” button.
//   .NET 10 (dotnet/aspnetcore#64228) otherwise stops in `resume-failed` and its
//   resume button fails the same way again.
// - Before reloading, `antumo:reconnect:before-reload` is dispatched on document
//   so the page can stash unsent input (sessionStorage) and restore it after.
// ============================================================================

export interface ReconnectTexts {
  connectingTitle: string;
  connectingText: string;
  retryingTitle: string;
  /** `{s}` = seconds to Blazor's next attempt. */
  retryingText: string;
  retryNow: string;
  reloadingTitle: string;
  reloadingText: string;
}

export interface ReconnectOptions {
  theme?: StatusTheme;
  showDelayMs?: number;
  /** Relative to <base href>. Must answer without auth and without a circuit. */
  healthUrl?: string;
  pollMs?: number;
  texts?: Partial<ReconnectTexts>;
  /** Override for tests. */
  reload?: () => void;
}

export const DEFAULT_RECONNECT_TEXTS: ReconnectTexts = {
  connectingTitle: 'Łączę ponownie z serwerem',
  connectingText: 'Zwykle trwa to kilka sekund.',
  retryingTitle: 'Serwer chwilowo nie odpowiada',
  retryingText: 'Ponawiam za {s} s.',
  retryNow: 'Spróbuj teraz',
  reloadingTitle: 'Nowa wersja aplikacji',
  reloadingText: 'Wczytuję stronę. Wpisane dane zostaną.',
};

const EVENT_BEFORE_RELOAD = 'antumo:reconnect:before-reload';

type BlazorApi = {
  reconnect?: () => Promise<boolean>;
  resumeCircuit?: () => Promise<boolean>;
};

const blazor = (): BlazorApi | undefined => (globalThis as unknown as { Blazor?: BlazorApi }).Blazor;

export class ReconnectOverlay {
  private static _instances = new WeakMap<HTMLElement, ReconnectOverlay>();

  private readonly _dialog: HTMLElement;
  private readonly _opts: Required<Omit<ReconnectOptions, 'texts' | 'reload'>> & {
    texts: ReconnectTexts;
    reload: () => void;
  };
  private _overlay: StatusOverlay | null = null;
  private _showTimer: ReturnType<typeof setTimeout> | null = null;
  private _pausedTimer: ReturnType<typeof setTimeout> | null = null;
  private _pollTimer: ReturnType<typeof setTimeout> | null = null;
  private _onUp: 'retry' | 'reload' | null = null;
  private _state = '';
  private _reloading = false;
  private _onVisible = (): void => {
    if (document.visibilityState === 'visible') void this.retry();
  };

  private constructor(dialog: HTMLElement, options: ReconnectOptions) {
    this._dialog = dialog;
    this._opts = {
      theme: options.theme ?? 'light',
      showDelayMs: options.showDelayMs ?? 1500,
      healthUrl: options.healthUrl ?? 'health/live',
      pollMs: options.pollMs ?? 2000,
      texts: { ...DEFAULT_RECONNECT_TEXTS, ...(options.texts ?? {}) },
      reload: options.reload ?? (() => location.reload()),
    };
    dialog.addEventListener('components-reconnect-state-changed', (e) =>
      this._onState((e as CustomEvent<{ state: string; secondsToNextAttempt?: number }>).detail),
    );
  }

  /** Wire one dialog. Idempotent. */
  static init(dialog: HTMLElement, options: ReconnectOptions = {}): ReconnectOverlay {
    const existing = ReconnectOverlay._instances.get(dialog);
    if (existing) return existing;
    const inst = new ReconnectOverlay(dialog, { ...ReconnectOverlay._fromData(dialog), ...options });
    ReconnectOverlay._instances.set(dialog, inst);
    return inst;
  }

  /** Wire every `[data-antumo-reconnect]` element (options from data-* attributes). */
  static autoInit(): void {
    document.querySelectorAll<HTMLElement>('[data-antumo-reconnect]').forEach((el) => ReconnectOverlay.init(el));
  }

  private static _fromData(el: HTMLElement): ReconnectOptions {
    const d = el.dataset;
    const texts: Partial<ReconnectTexts> = {};
    (Object.keys(DEFAULT_RECONNECT_TEXTS) as (keyof ReconnectTexts)[]).forEach((k) => {
      const v = d[`text${k[0].toUpperCase()}${k.slice(1)}`];
      if (v) texts[k] = v;
    });
    const num = (v: string | undefined): number | undefined => (v && Number.isFinite(+v) ? +v : undefined);
    return {
      // Not data-theme: [data-theme="dark"] switches the DS dark tokens for the whole subtree.
      theme: d.statusTheme === 'dark' ? 'dark' : undefined,
      showDelayMs: num(d.showDelay),
      healthUrl: d.healthUrl || undefined,
      pollMs: num(d.pollMs),
      texts,
    };
  }

  get state(): string {
    return this._state;
  }

  // ── Blazor state machine ─────────────────────────────────────────────────

  private _onState(detail: { state: string; secondsToNextAttempt?: number }): void {
    const state = detail.state;
    this._state = state;
    const t = this._opts.texts;
    switch (state) {
      case 'show':
        this._clearShow();
        this._showTimer = setTimeout(() => {
          this._showTimer = null;
          this._show({ tone: 'neutral', title: t.connectingTitle, text: t.connectingText, countdown: null, actions: [] });
          this._poll('retry');
        }, this._opts.showDelayMs);
        break;
      case 'retrying':
        if (this._overlay && (detail.secondsToNextAttempt ?? 0) > 1) {
          this._show({
            tone: 'warning',
            title: t.retryingTitle,
            text: t.retryingText,
            countdown: detail.secondsToNextAttempt ?? null,
            actions: [{ label: t.retryNow, onClick: () => void this.retry() }],
          });
        }
        break;
      case 'hide':
        this._clearShow();
        this._stopPoll();
        this._overlay?.hide();
        this._overlay = null;
        document.removeEventListener('visibilitychange', this._onVisible);
        break;
      case 'failed':
      case 'resume-failed':
        this._show({ tone: 'warning', title: t.retryingTitle, text: '', countdown: null, actions: [{ label: t.retryNow, onClick: () => void this.retry() }] });
        this._poll('reload');
        if (state === 'failed') document.addEventListener('visibilitychange', this._onVisible);
        break;
      case 'paused':
        // Also sent briefly while Blazor itself tries to resume: only a pause that
        // persists means the circuit is gone.
        this._show({ tone: 'neutral', title: t.connectingTitle, text: t.connectingText, countdown: null, actions: [] });
        if (this._pausedTimer) clearTimeout(this._pausedTimer);
        this._pausedTimer = setTimeout(() => {
          this._pausedTimer = null;
          if (this._state === 'paused' || this._state === 'resume-failed') this._poll('reload');
        }, 3000);
        break;
      case 'rejected':
        this.reload();
        break;
    }
  }

  /** Try the circuit now; when the server answers but the circuit is gone, reload. */
  async retry(): Promise<void> {
    document.removeEventListener('visibilitychange', this._onVisible);
    const api = blazor();
    try {
      if (api?.reconnect && (await api.reconnect())) return;
      if (api?.resumeCircuit && (await api.resumeCircuit())) {
        this._overlay?.hide();
        this._overlay = null;
        return;
      }
      this.reload();
    } catch {
      // Server unreachable, or the old circuit died mid-resume: reload once it answers.
      this._poll('reload');
      document.addEventListener('visibilitychange', this._onVisible);
    }
  }

  reload(): void {
    if (this._reloading) return;
    this._reloading = true;
    this._stopPoll();
    const t = this._opts.texts;
    this._show({ tone: 'success', title: t.reloadingTitle, text: t.reloadingText, countdown: null, actions: [] });
    try {
      document.dispatchEvent(new CustomEvent(EVENT_BEFORE_RELOAD));
    } catch {
      /* stashing is best effort */
    }
    this._opts.reload();
  }

  // ── helpers ──────────────────────────────────────────────────────────────

  private _show(opts: Partial<StatusOverlayOptions> & { title: string }): void {
    const full: StatusOverlayOptions = { theme: this._opts.theme, ...opts };
    if (this._overlay) this._overlay.update(full);
    else this._overlay = StatusOverlay.attach(this._dialog, full);
  }

  private _clearShow(): void {
    if (this._showTimer) {
      clearTimeout(this._showTimer);
      this._showTimer = null;
    }
  }

  /** Poll the health URL; run `onUp` once it answers. A reload request wins over a retry. */
  private _poll(onUp: 'retry' | 'reload'): void {
    if (this._reloading) return;
    if (this._pollTimer !== null) {
      if (onUp === 'reload') this._onUp = 'reload';
      return;
    }
    this._onUp = onUp;
    const tick = async (): Promise<void> => {
      if (this._reloading) return;
      const up = await this._serverUp();
      if (this._pollTimer === null) return;
      if (up) {
        this._pollTimer = null;
        const action = this._onUp;
        this._onUp = null;
        if (action === 'reload') this.reload();
        else if (action === 'retry') await this.retry();
        return;
      }
      this._pollTimer = setTimeout(tick, this._opts.pollMs);
    };
    this._pollTimer = setTimeout(tick, 0);
  }

  private _stopPoll(): void {
    if (this._pollTimer !== null) clearTimeout(this._pollTimer);
    this._pollTimer = null;
    this._onUp = null;
  }

  private async _serverUp(): Promise<boolean> {
    try {
      const r = await fetch(this._opts.healthUrl, { cache: 'no-store', credentials: 'same-origin' });
      return r.ok;
    } catch {
      return false;
    }
  }
}
